<template>
    <form v-if="status.detail" @submit.prevent="submit">
        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.key')"><b-form-input :value="form.key" readonly />
                </b-form-group>
            </div>
            <div class="col-md">
                <b-form-group :label="$t('label.keyValueType')">
                    <b-form-input :value="valueTypeName" readonly />
                </b-form-group>
            </div>
        </div>
        <b-form-group :label="$t('label.keyValueHint')"><b-form-input :value="form.keyValueHint" /></b-form-group>
        <b-form-group :label="$t('label.status')">
            <b-form-checkbox v-model="form.isActive" switch>
                {{ form.isActive ? $t('label.active') : $t('label.inactive') }}
            </b-form-checkbox>
        </b-form-group>
        <hr>
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">{{ $t('label.configurationValues') }}</h6>
            <small class="text-muted">{{ $tc('label.valuesRemaining', remainingValues, [remainingValues]) }}</small>
        </div>
        <div v-for="(item, index) in form.values" :key="item.uuid || index" class="border rounded p-3 mb-2">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <strong>{{ $t('label.value') }} {{ index + 1 }}</strong>
                <b-button type="button" variant="outline-danger" size="sm" :title="$t('label.removeValue')"
                    @click="removeValue(index)">
                    <i class="mdi mdi-delete-outline"></i>
                </b-button>
            </div>
            <b-form-group>
                <b-form-select v-if="form.keyValueType === valueTypes.BOOLEAN" v-model="item.value"
                    :options="booleanOptions" required />
                <b-form-textarea v-else-if="isStructuredType" v-model="item.value" rows="4" required />
                <b-form-input v-else v-model="item.value" :type="inputType" :step="inputStep" required />
            </b-form-group>
            <b-form-checkbox v-model="item.isActive" switch>{{ item.isActive ? $t('label.active') : $t('label.inactive')
            }}</b-form-checkbox>
        </div>
        <b-button v-if="remainingValues > 0" type="button" variant="outline-info" size="sm" @click="addValue">
            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.addValue') }}
        </b-button>
        <div class="d-flex justify-content-end mt-3">
            <b-button variant="danger" class="mr-2" @click="$emit('done')">{{ $t('label.cancel') }}</b-button>
            <b-button type="submit" variant="info" :disabled="!status.save"><span v-if="status.save">{{ $t('label.save')
            }}</span><b-spinner v-else small /></b-button>
        </div>
    </form>
    <div v-else class="text-center p-5"><b-spinner variant="info" /></div>
</template>

<script>
import { mapState } from 'vuex';
import { configurationMethods } from '@/state/api';
import { CONFIGURATION_MODULE } from '@/state/api/configuration.state';
import { CONFIGURATION_VALUE_TYPE } from '@/constants/enums';

export default {
    name: 'ConfigurationForm',
    props: {
        configurationUUID: { type: String, required: true },
        searchCriteria: { type: Object, required: true }
    },
    data() {
        return {
            valueTypes: CONFIGURATION_VALUE_TYPE,
            form: {
                uuid: '',
                key: '',
                number: 0,
                keyValueType: '',
                keyValueHint: '',
                isActive: false,
                values: []
            }
        };
    },
    computed: {
        ...mapState(CONFIGURATION_MODULE, ['status']),
        valueTypeName() {
            const names = {
                [CONFIGURATION_VALUE_TYPE.STRING]: 'label.configurationString',
                [CONFIGURATION_VALUE_TYPE.INTEGER]: 'label.configurationInteger',
                [CONFIGURATION_VALUE_TYPE.DOUBLE]: 'label.configurationDouble',
                [CONFIGURATION_VALUE_TYPE.BOOLEAN]: 'label.configurationBoolean',
                [CONFIGURATION_VALUE_TYPE.ARRAY]: 'label.configurationArray',
                [CONFIGURATION_VALUE_TYPE.DATE]: 'label.configurationDate',
                [CONFIGURATION_VALUE_TYPE.DATE_TIME]: 'label.configurationDateTime',
                [CONFIGURATION_VALUE_TYPE.JSON]: 'label.configurationJson',
            };
            return names[this.form.keyValueType] ? this.$t(names[this.form.keyValueType]) : this.form.keyValueType;
        },
        isStructuredType() {
            return [CONFIGURATION_VALUE_TYPE.ARRAY, CONFIGURATION_VALUE_TYPE.JSON].includes(this.form.keyValueType);
        },
        inputType() {
            if ([CONFIGURATION_VALUE_TYPE.INTEGER, CONFIGURATION_VALUE_TYPE.DOUBLE].includes(this.form.keyValueType)) {
                return 'number'
            }
            if (this.form.keyValueType === CONFIGURATION_VALUE_TYPE.DATE) {
                return 'date';
            }
            if (this.form.keyValueType === CONFIGURATION_VALUE_TYPE.DATE_TIME) {
                return 'datetime-local';
            }
            return 'text';
        },
        inputStep() {
            return this.form.keyValueType === CONFIGURATION_VALUE_TYPE.INTEGER ? '1' : 'any';
        },
        booleanOptions() {
            return [{ value: 'true', text: this.$t('label.true') }, { value: 'false', text: this.$t('label.false') }];
        },
        remainingValues() {
            return Math.max(0, (Number(this.form.number) || 0) - this.form.values.length);
        },
    },
    async created() {
        const result = await this.getConfiguration({ uuid: this.configurationUUID });
        if (result) {
            this.form = JSON.parse(JSON.stringify(this.$store.state.api.configuration.details));
            this.syncValueRows();
        }
    },
    methods: {
        ...configurationMethods,
        syncValueRows() {
            const count = Math.max(0, Number(this.form.number) || 0);
            const values = Array.isArray(this.form.values) ? this.form.values.slice(0, count) : [];
            if (this.form.keyValueType === CONFIGURATION_VALUE_TYPE.BOOLEAN) {
                values.forEach((item) => { item.value = ['true', '1', 1, true].includes(item.value) ? 'true' : 'false'; });
            }
            if (this.form.keyValueType === CONFIGURATION_VALUE_TYPE.DATE_TIME) {
                values.forEach((item) => { if (item.value) item.value = String(item.value).slice(0, 16); });
            }
            this.form.values = values;
        },
        addValue() {
            if (this.remainingValues <= 0) return;
            this.form.values.push({
                uuid: '',
                value: this.form.keyValueType === CONFIGURATION_VALUE_TYPE.BOOLEAN ? 'false' : '',
                isActive: true,
            });
        },
        async removeValue(index) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t('label.confirmRemoveConfigurationValue'),
                {
                    title: this.$t('label.confirmDelete'),
                    okTitle: this.$t('label.delete'),
                    okVariant: 'danger',
                    cancelTitle: this.$t('label.cancel'),
                    cancelVariant: 'outline-secondary',
                    centered: true,
                },
            );
            if (confirmed) this.form.values.splice(index, 1);
        },
        async submit() {
            const result = await this.updateConfiguration({ data: this.form, filter: this.searchCriteria });
            if (result) {
                this.$emit('done');
            }
        },
    },
};
</script>
